import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FiBriefcase,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUser,
} from "react-icons/fi";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { extractApiErrorDetail } from "../../services/errorHandler";
import {
  useEssDashboard,
  useEssLeaveMyRequests,
  useEssOvertimeMyRequests,
} from "../../services/essService";

const LABEL_MUTED = "#64748b";

const formatShortDate = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const isPendingStatus = (s) => String(s || "").trim().toLowerCase() === "pending";

const formatClockLabel = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

function humanizeAttendanceStatus(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
}

function DashboardMetric({ label, value, hint }) {
  return (
    <Box
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      px={5}
      py={4}
      borderWidth="1px"
      borderColor="gray.100"
    >
      <Text fontSize="12px" fontWeight="600" color={LABEL_MUTED} letterSpacing="0.02em">
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="700" color="gray.800" mt={1} lineHeight="short">
        {value}
      </Text>
      {hint ? (
        <Text fontSize="11px" color="gray.500" mt={1.5} lineHeight="short">
          {hint}
        </Text>
      ) : null}
    </Box>
  );
}

const EssHome = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const {
    essDashboard,
    essDashboardIsLoading,
    essDashboardError,
    refetchEssDashboard,
    linked,
    portal,
  } = useEssDashboard();

  const overview = portal?.overview ?? essDashboard?.overview ?? {};

  const feedEnabled = Boolean(essDashboard && linked !== false && !essDashboardError);
  const { leaveRequests } = useEssLeaveMyRequests({ enabled: feedEnabled });
  const { overtimeRequests } = useEssOvertimeMyRequests({ enabled: feedEnabled });

  const errDetail = essDashboardError ? extractApiErrorDetail(essDashboardError) : null;

  const pendingLeaveCount = useMemo(
    () =>
      Array.isArray(leaveRequests)
        ? leaveRequests.filter((r) => isPendingStatus(r.status)).length
        : 0,
    [leaveRequests],
  );
  const pendingOtCount = useMemo(
    () =>
      Array.isArray(overtimeRequests)
        ? overtimeRequests.filter((r) => isPendingStatus(r.status)).length
        : 0,
    [overtimeRequests],
  );
  const pendingTotal = pendingLeaveCount + pendingOtCount;

  const recentActivity = useMemo(() => {
    if (!Array.isArray(leaveRequests) || !Array.isArray(overtimeRequests)) return [];
    const items = [];
    for (const row of leaveRequests) {
      const st = String(row.status || "").toLowerCase();
      const start = formatShortDate(row.leave_start);
      const end = formatShortDate(row.leave_end);
      const range = start && end && start !== end ? `${start}–${end}` : start || end || "";
      let msg = "";
      if (st === "pending") {
        msg = `Leave request pending · ${row.entry_type || "Leave"}`;
      } else if (st === "approved") {
        msg = `Leave approved`;
      } else if (st === "rejected") {
        msg = `Leave rejected`;
      } else if (st === "recalled") {
        msg = `Leave recalled`;
      } else {
        msg = `Leave ${row.status || "updated"}`;
      }
      if (range) msg = `${msg} · ${range}`;
      const ts = new Date(row.leave_end || row.leave_start || 0).getTime();
      items.push({ id: `l-${row.leave_id}`, ts, msg, icon: FiCalendar });
    }
    for (const row of overtimeRequests) {
      const st = String(row.status || "").toLowerCase();
      const hrs = row.hours_requested ?? "—";
      const dateBit = row.date ? ` · ${row.date}` : "";
      let msg = "";
      if (st === "pending") msg = `Overtime pending · ${hrs}h${dateBit}`;
      else if (st === "approved") msg = `Overtime approved · ${hrs}h`;
      else if (st === "rejected") msg = `Overtime rejected · ${hrs}h`;
      else msg = `Overtime ${row.status || "updated"} · ${hrs}h`;
      const ts = new Date(row.created_at || row.date || 0).getTime();
      items.push({ id: `o-${row.request_id}`, ts, msg, icon: FiClock });
    }
    items.sort((a, b) => b.ts - a.ts);
    return items.slice(0, 5);
  }, [leaveRequests, overtimeRequests]);

  const showSoon = (label) => {
    toast({
      title: `${label} — coming soon`,
      description: "This isn’t available in self-service yet. Contact HR if you need help.",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  };

  if (essDashboardIsLoading && !essDashboard) {
    return (
      <Flex justify="center" py={24}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
      </Flex>
    );
  }

  if (errDetail?.code === "ESS_ONLY") {
    return (
      <Alert status="warning" borderRadius="xl" maxW="lg" boxShadow="sm">
        <AlertIcon />
        <Box>
          <Text fontWeight="600">{errDetail.message || "Wrong account type"}</Text>
          <Text fontSize="sm" mt={2} color="gray.700">
            For the admin portal, sign out and use an administrator account.
          </Text>
        </Box>
      </Alert>
    );
  }

  if (errDetail?.code === "NO_LINKED_EMPLOYEE" && !portal) {
    return (
      <VStack align="stretch" spacing={4} maxW="md">
        <Alert status="warning" borderRadius="xl">
          <AlertIcon />
          {errDetail.message || "No employee record is linked to this account."}
        </Alert>
        <Button size="sm" colorScheme="blue" variant="outline" onClick={() => refetchEssDashboard()}>
          Retry
        </Button>
      </VStack>
    );
  }

  if (essDashboardError && !essDashboard) {
    return (
      <VStack align="stretch" spacing={4} maxW="md">
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {errDetail?.message || "Could not load your dashboard."}
        </Alert>
        <Button size="sm" colorScheme="blue" onClick={() => refetchEssDashboard()}>
          Retry
        </Button>
      </VStack>
    );
  }

  const hero = portal?.hero ?? {};
  const actions = portal?.actions ?? {};
  const today = portal?.today ?? null;

  const lb = overview.leave_balance;
  const at = overview.attendance_today;
  const pr = overview.pending_requests;

  let leaveBalanceDisplay = "—";
  let leaveBalanceHint = "Not available in the portal yet";
  if (lb && typeof lb.remaining_days === "number") {
    const rem = lb.remaining_days;
    leaveBalanceDisplay = `${rem} day${rem === 1 ? "" : "s"} left`;
    const hintParts = [];
    if (lb.annual_days != null) hintParts.push(`${lb.annual_days} annual`);
    if (lb.used_days != null) hintParts.push(`${lb.used_days} used`);
    if (lb.pending_days != null && Number(lb.pending_days) > 0) {
      hintParts.push(`${lb.pending_days} pending approval`);
    }
    if (lb.year != null && !hintParts.length) hintParts.push(`Year ${lb.year}`);
    leaveBalanceHint = hintParts.join(" · ") || (lb.year != null ? `Year ${lb.year}` : "");
  } else {
    const metrics = portal?.metrics ?? {};
    const leaveBalanceRaw =
      metrics.leave_balance_days ??
      metrics.leave_balance ??
      portal?.leave_balance_days ??
      null;
    if (leaveBalanceRaw != null && leaveBalanceRaw !== "") {
      leaveBalanceDisplay = `${leaveBalanceRaw} day${Number(leaveBalanceRaw) === 1 ? "" : "s"}`;
      leaveBalanceHint = "Annual leave balance";
    }
  }

  let attendanceDisplay = "—";
  let attendanceHint = "Clock-in isn’t shown in self-service yet";
  if (at && (at.attendance_status != null && at.attendance_status !== "" || at.status != null && at.status !== "")) {
    attendanceDisplay =
      humanizeAttendanceStatus(at.attendance_status) ||
      humanizeAttendanceStatus(String(at.status).replace(/_/g, " "));
    const hintParts = [];
    const inL = formatClockLabel(at.clock_in);
    const outL = formatClockLabel(at.clock_out);
    if (inL) hintParts.push(`In ${inL}`);
    if (outL) hintParts.push(`Out ${outL}`);
    if (at.total_hours != null && at.total_hours !== "") hintParts.push(`${at.total_hours}h`);
    if (at.date) hintParts.push(at.date);
    attendanceHint = hintParts.filter(Boolean).join(" · ") || "";
  } else if (today?.approved_leave_today) {
    attendanceDisplay = "On leave today";
    attendanceHint = today.leave_type_name ? String(today.leave_type_name) : "Approved leave";
  } else if (today?.attendance_label) {
    attendanceDisplay = String(today.attendance_label);
    attendanceHint = today?.attendance_detail ? String(today.attendance_detail) : "";
  } else if (today?.clock_status) {
    attendanceDisplay = String(today.clock_status);
    attendanceHint = "";
  }

  let pendingLabel = pendingTotal === 0 ? "None" : String(pendingTotal);
  let pendingHint =
    pendingTotal === 0
      ? "No pending leave or overtime"
      : [
          pendingLeaveCount ? `${pendingLeaveCount} leave` : null,
          pendingOtCount ? `${pendingOtCount} overtime` : null,
        ]
          .filter(Boolean)
          .join(" · ");

  if (pr && typeof pr.total === "number") {
    pendingLabel = pr.total === 0 ? "None" : String(pr.total);
    pendingHint =
      pr.total === 0
        ? "No pending leave or overtime"
        : [
            pr.leave != null && pr.leave > 0 ? `${pr.leave} leave` : null,
            pr.overtime != null && pr.overtime > 0 ? `${pr.overtime} overtime` : null,
          ]
            .filter(Boolean)
            .join(" · ") || "Leave & overtime requests";
  }

  const onLeaveBanner =
    !!today?.approved_leave_today ||
    (() => {
      const st = `${at?.status ?? ""} ${at?.attendance_status ?? ""}`.toLowerCase();
      return st.includes("leave") && !st.includes("no leave");
    })();

  const name = hero.display_name || "Employee";
  const designation = hero.designation || "";
  const departmentName = hero.department || "";
  const locationLine = hero.location_label || "";
  const branchHero = hero.branch_label || "";
  const status = hero.status || "—";

  const heroSub = [designation, departmentName].filter(Boolean).join(" • ");
  const isLinked = linked !== false;

  const profileImg = (hero.profile_image || "").trim() || undefined;

  return (
    <VStack align="stretch" spacing={{ base: 6, md: 8 }} maxW="1024px" pb={10}>
      <Box>
        <Text
          fontSize="xs"
          fontWeight="700"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={3}
        >
          Overview
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
          <DashboardMetric label="Leave balance" value={leaveBalanceDisplay} hint={leaveBalanceHint} />
          <DashboardMetric label="Attendance today" value={attendanceDisplay} hint={attendanceHint} />
          <DashboardMetric label="Pending requests" value={pendingLabel} hint={pendingHint} />
        </SimpleGrid>
      </Box>

      <Box
        bg="white"
        borderRadius="2xl"
        boxShadow="sm"
        px={{ base: 5, md: 6 }}
        py={{ base: 5, md: 6 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          right={0}
          w={{ base: "100%", lg: "45%" }}
          h="100%"
          bgGradient="linear(to-l, blue.50, transparent)"
          pointerEvents="none"
        />
        {!isLinked && (
          <Alert status="info" borderRadius="lg" mb={5} size="sm">
            <AlertIcon />
            <Text fontSize="sm">
              Your account isn’t linked to an employee record yet. Some sections stay empty until HR completes setup.
            </Text>
          </Alert>
        )}
        {onLeaveBanner && (
          <Alert status="success" borderRadius="lg" mb={5} size="sm" variant="subtle">
            <AlertIcon />
            <Text fontSize="sm">
              <strong>On leave today</strong>
              {today?.leave_type_name ? ` · ${today.leave_type_name}` : ""}
            </Text>
          </Alert>
        )}
        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={{ base: 6, lg: 8 }}
          align={{ lg: "flex-start" }}
          position="relative"
        >
          <Flex
            direction={{ base: "column", sm: "row" }}
            gap={6}
            align={{ sm: "center" }}
            flex={1}
            minW={0}
          >
            <Avatar size="2xl" name={name} src={profileImg} flexShrink={0} />
            <VStack align="stretch" spacing={3} flex={1}>
              <Box>
                <Heading as="h1" size="lg" color="gray.900" fontWeight="800" letterSpacing="-0.02em">
                  {name}
                </Heading>
                {heroSub && (
                  <Text fontSize="md" color="gray.600" fontWeight="500" mt={1}>
                    {heroSub}
                  </Text>
                )}
                {locationLine && (
                  <HStack mt={2} spacing={2} color="gray.600">
                    <Icon as={FiMapPin} boxSize={4} />
                    <Text fontSize="sm">{locationLine}</Text>
                  </HStack>
                )}
                {branchHero && (
                  <HStack mt={1} spacing={2} color="gray.600">
                    <Icon as={FiBriefcase} boxSize={4} />
                    <Text fontSize="sm">{branchHero}</Text>
                  </HStack>
                )}
              </Box>
              <HStack flexWrap="wrap" gap={2}>
                <Badge
                  colorScheme={String(status).toLowerCase() === "active" ? "green" : "gray"}
                  fontSize="0.7rem"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  textTransform="capitalize"
                >
                  {status}
                </Badge>
              </HStack>
              <HStack flexWrap="wrap" spacing={2} pt={1}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<FiUser />}
                  onClick={() => navigate("/ess/profile")}
                >
                  View profile
                </Button>
              </HStack>
            </VStack>
          </Flex>

          <Box
            w={{ base: "100%", lg: "300px" }}
            flexShrink={0}
            bg="whiteAlpha.800"
            backdropFilter="blur(6px)"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="gray.100"
            px={4}
            py={4}
            alignSelf="stretch"
          >
            <Text fontSize="sm" fontWeight="700" color="gray.800" letterSpacing="-0.01em" mb={3}>
              Recent activity
            </Text>
            {recentActivity.length === 0 ? (
              <Text fontSize="sm" color="gray.500" lineHeight="1.5">
                No recent leave or overtime activity yet. Submit a request and it will show up here.
              </Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {recentActivity.map((item) => (
                  <HStack key={item.id} align="flex-start" spacing={2}>
                    <Icon as={item.icon} color="blue.500" mt={0.5} boxSize={4} flexShrink={0} />
                    <Text fontSize="sm" fontWeight="500" color="gray.700" lineHeight="short">
                      {item.msg}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>
        </Flex>
      </Box>

      <Box px={{ base: 0, md: 1 }}>
        <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
          Quick actions
        </Text>
        <Flex gap={3} flexWrap="wrap">
          <Tooltip
            label={
              !isLinked || actions.can_apply_leave === false
                ? "Leave isn’t available from the portal right now. Contact HR."
                : ""
            }
            isDisabled={isLinked && actions.can_apply_leave !== false}
          >
            <Button
              colorScheme="blue"
              size="lg"
              leftIcon={<FiCalendar />}
              borderRadius="xl"
              onClick={() => {
                if (!isLinked || actions.can_apply_leave === false) return;
                navigate("/ess/leave");
              }}
              boxShadow="sm"
              isDisabled={!isLinked || actions.can_apply_leave === false}
            >
              Apply leave
            </Button>
          </Tooltip>

          <Tooltip
            label={
              !isLinked || actions.can_request_overtime === false
                ? "Overtime requests aren’t enabled for you right now. Contact HR."
                : ""
            }
            isDisabled={isLinked && actions.can_request_overtime !== false}
          >
            <Button
              size="lg"
              variant="outline"
              leftIcon={<FiClock />}
              borderRadius="xl"
              borderColor="gray.200"
              onClick={() => {
                if (!isLinked || actions.can_request_overtime === false) return;
                navigate("/ess/overtime");
              }}
              isDisabled={!isLinked || actions.can_request_overtime === false}
            >
              Request overtime
            </Button>
          </Tooltip>

          <Tooltip
            label={
              actions.payslip_available
                ? ""
                : "Pay slips aren’t available here yet. HR or payroll will share payslips when the feature goes live."
            }
            isDisabled={!!actions.payslip_available}
          >
            <Button
              size="lg"
              variant="ghost"
              colorScheme="gray"
              leftIcon={<Icon as={FiBriefcase} />}
              borderRadius="xl"
              onClick={() => {
                if (actions.payslip_available) showSoon("Payslip");
                else {
                  toast({
                    title: "Payslips",
                    description:
                      "Payslips aren’t available in the portal yet. Contact payroll or HR for your statements.",
                    status: "info",
                    duration: 6000,
                    isClosable: true,
                  });
                }
              }}
              isDisabled={!actions.payslip_available}
            >
              Payslip
            </Button>
          </Tooltip>
        </Flex>
      </Box>
    </VStack>
  );
};

export default EssHome;
